<template>
  <div class="line-property">
    <n-collapse :default-expanded-names="defaultExpanded" class="pm-collapse">
      <n-collapse-item title="信息" name="info">
        <div class="info-box">
          <n-text depth="3">
            {{ infoText }}
          </n-text>
        </div>
      </n-collapse-item>

      <n-collapse-item title="方向" name="direction">
        <n-radio-group
          :value="options.direction"
          size="small"
          :disabled="!canEdit"
          @update:value="handleDirectionChange"
        >
          <n-space vertical size="small">
            <n-radio :value="LineDirection.AsSketched">按绘制方向</n-radio>
            <n-radio :value="LineDirection.Horizontal">水平</n-radio>
            <n-radio :value="LineDirection.Vertical">竖直</n-radio>
            <n-radio :value="LineDirection.Angle">角度</n-radio>
          </n-space>
        </n-radio-group>
        <div v-if="options.direction === LineDirection.Angle" class="direction-angle">
          <n-text depth="3" class="param-label">角度</n-text>
          <n-input-number
            :value="options.angle"
            size="small"
            :disabled="!canEdit"
            :show-button="true"
            :precision="8"
            :step="1"
            @update:value="handleAngleInput"
          />
        </div>
      </n-collapse-item>

      <n-collapse-item title="选项" name="options">
        <div class="option-list">
          <n-checkbox
            :checked="constructionChecked"
            :disabled="!canEdit || isCenterline"
            @update:checked="handleConstructionToggle"
          >
            作为构造线
          </n-checkbox>
          <n-checkbox
            :checked="options.infiniteLength"
            :disabled="!canEdit"
            @update:checked="handleInfiniteToggle"
          >
            无限长度
          </n-checkbox>
          <n-checkbox
            :checked="isMidpoint"
            :disabled="!canEdit || isCenterline"
            @update:checked="handleMidpointToggle"
          >
            中点线
          </n-checkbox>
        </div>
      </n-collapse-item>

      <n-collapse-item v-if="hasStarted" title="参数" name="parameters">
        <div class="param-grid">
          <div class="param-row">
            <n-text depth="3" class="param-label">长度</n-text>
            <n-input-number
              :value="lineLengthDisplay"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :step="1"
              @update:value="handleLengthInput"
            />
          </div>
          <div class="param-row">
            <n-text depth="3" class="param-label">角度</n-text>
            <n-input-number
              :value="angleValue"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :step="1"
              @update:value="handleParamAngleInput"
            />
          </div>
        </div>
      </n-collapse-item>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import type { Subscription } from 'rxjs';
import {
  NCheckbox,
  NCollapse,
  NCollapseItem,
  NInputNumber,
  NRadio,
  NRadioGroup,
  NSpace,
  NText,
} from 'naive-ui';
import {
  LineDirection,
} from '@/core';
import type {
  LineOptions,
  LineParameterUpdate,
  LineParameters,
} from '@/core';
import { useViewer } from '@/hooks';
import { convertMToMm, convertMmToM, SketchLineCommand } from '@puppycad/cad-core';

defineOptions({
  inheritAttrs: false,
});

const { getViewer } = useViewer();

/**
 * @description 绘图态默认线选项。
 */
const defaultOptions: LineOptions = {
  type: SketchLineCommand.Line,
  direction: LineDirection.AsSketched,
  angle: 0,
  forConstruction: false,
  infiniteLength: false,
};

/**
 * @description 本地线选项快照。
 */
const localLineOptions = ref<LineOptions>({ ...defaultOptions });

/**
 * @description 本地线参数快照。
 */
const localLineParameters = ref<LineParameters | null>(null);

/**
 * @description 当前线绘制点数量快照。
 */
const localLineDrawingPointCount = ref(0);

/**
 * @description 同步渲染器中的线条绘制状态到本地快照。
 */
function syncLineStateFromRenderer(): void {
  const renderer = getViewer();
  const lineOptions = renderer?.getLineDrawingOptions?.();
  if (!renderer || !lineOptions) {
    localLineOptions.value = { ...defaultOptions };
    localLineParameters.value = null;
    localLineDrawingPointCount.value = 0;
    return;
  }

  localLineOptions.value = { ...lineOptions };
  const lineParameters = renderer.getLineDrawingParameters?.() ?? null;
  localLineParameters.value = lineParameters ? { ...lineParameters } : null;
}

let lineStateSyncTimer: number | null = null;
let lineDrawingPointCountSubscription: Subscription | null = null;

onMounted(() => {
  syncLineStateFromRenderer();
  lineDrawingPointCountSubscription = getViewer()?.observeLineDrawingPointCount?.().subscribe((pointCount) => {
    localLineDrawingPointCount.value = pointCount;
  }) ?? null;
});

onBeforeUnmount(() => {
  if (lineStateSyncTimer !== null) {
    clearInterval(lineStateSyncTimer);
    lineStateSyncTimer = null;
  }
  lineDrawingPointCountSubscription?.unsubscribe();
  lineDrawingPointCountSubscription = null;
});

/**
 * @description 当前线选项。
 */
const options = computed(() => localLineOptions.value);

/**
 * @description 当前线参数（绘图态）。
 */
const lineParameters = computed(() => localLineParameters.value);

/**
 * @description 当前是否已有绘图起点。
 */
const hasStarted = computed(() => localLineDrawingPointCount.value > 0);

/**
 * @description 绘图态是否允许编辑线选项。
 */
const canEdit = computed(() => Boolean(getViewer()));

/**
 * @description 绘图态是否允许编辑线参数。
 */
const canEditParameters = computed(() => hasStarted.value && Boolean(getViewer()));

/**
 * @description 是否中心线。
 */
const isCenterline = computed(() => options.value.type === SketchLineCommand.CenterLine);

/**
 * @description 是否中点线。
 */
const isMidpoint = computed(() => options.value.type === SketchLineCommand.MidpointLine);

/**
 * @description 构造线勾选态（中心线强制为真）。
 */
const constructionChecked = computed(() => (isCenterline.value ? true : options.value.forConstruction));

/**
 * @description 参数区角度值（优先参数区，回退到选项角度）。
 */
const angleValue = computed(() => lineParameters.value?.angle ?? options.value.angle ?? null);
/**
 * @description 绘图态线长度的毫米显示值。
 */
const lineLengthDisplay = computed(() => {
  const length = lineParameters.value?.length ?? null;
  return length === null ? null : Number(convertMToMm(length).toFixed(8));
});

const infoText = computed(() => {
  if (!hasStarted.value) {
    return '编辑下一条线的设置并绘制新线段。';
  }
  return '继续当前线设置，或继续绘制下一段。';
});

/**
 * @description 绘图态默认展开分组。
 */
const defaultExpanded = ['info', 'direction', 'options', 'parameters'];

/**
 * @description 更新绘图态线选项。
 * @param patch 选项补丁。
 */
function updateOptions(patch: Partial<LineOptions>): void {
  const renderer = getViewer();
  const currentOptions = renderer?.getLineDrawingOptions?.();
  if (!renderer || !currentOptions) {
    return;
  }

  const nextPatch = { ...patch };
  const nextType = nextPatch.type ?? currentOptions.type;
  if (nextType === SketchLineCommand.CenterLine) {
    nextPatch.forConstruction = true;
  }

  renderer.setLineDrawingOptions?.({
    ...currentOptions,
    ...nextPatch,
  });
  syncLineStateFromRenderer();
}

/**
 * @description 更新线参数并同步预览。
 * @param update 参数更新描述。
 */
function updateLineParameters(update: LineParameterUpdate): void {
  const renderer = getViewer();
  if (!renderer) {
    return;
  }
  syncLineStateFromRenderer();
}

/**
 * @description 切换绘图方向。
 * @param value 方向值。
 */
function handleDirectionChange(value: LineDirection): void {
  updateOptions({ direction: value });
}

/**
 * @description 处理方向区角度输入。
 * @param value 输入值。
 */
function handleAngleInput(value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    return;
  }
  updateOptions({ direction: LineDirection.Angle, angle: value });
  updateLineParameters({ type: 'angle', value });
}

/**
 * @description 绘图态切换构造线。
 * @param value 新状态。
 */
function handleConstructionToggle(value: boolean): void {
  if (isCenterline.value) {
    return;
  }
  updateOptions({ forConstruction: value });
}

/**
 * @description 绘图态切换无限长度。
 * @param value 新状态。
 */
function handleInfiniteToggle(value: boolean): void {
  updateOptions({ infiniteLength: value });
}

/**
 * @description 绘图态切换中点线。
 * @param value 新状态。
 */
function handleMidpointToggle(value: boolean): void {
  if (isCenterline.value) {
    return;
  }
  updateOptions({
    type: value
      ? SketchLineCommand.MidpointLine
      : SketchLineCommand.Line,
  });
}

/**
 * @description 处理参数区长度输入（绘图态）。
 * @param value 输入值。
 */
function handleLengthInput(value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    return;
  }
  // 用户输入的长度以毫米为单位，写入绘图参数前转换为米。
  updateLineParameters({ type: 'length', value: convertMmToM(value) });
}

/**
 * @description 处理参数区角度输入（绘图态）。
 * @param value 输入值。
 */
function handleParamAngleInput(value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    return;
  }
  updateOptions({ direction: LineDirection.Angle, angle: value });
  updateLineParameters({ type: 'angle', value });
}

/**
 * @description 添加关系（选中线态）。
 * @param type 关系类型。
 */
</script>

<style scoped>
.line-property {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pm-collapse :deep(.n-collapse-item__header) {
  padding: 6px 0;
}

.info-box {
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(234, 179, 8, 0.15);
  border: 1px solid rgba(234, 179, 8, 0.35);
}

.direction-angle {
  margin-top: 10px;
  display: grid;
  grid-template-columns: 48px 1fr;
  gap: 8px;
  align-items: center;
}

.option-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.param-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.param-row {
  display: grid;
  grid-template-columns: 48px 1fr;
  gap: 8px;
  align-items: center;
}

.param-label {
  font-size: 12px;
}
</style>
